import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import { DragHandle as DragHandleIcon, Book, Audiotrack } from '@mui/icons-material';
import { DndContext, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const DragHandle = React.memo((props) => (
  <IconButton
    {...props.listeners}
    {...props.attributes}
    size="small"
    title="Drag to reorder"
    sx={{ cursor: 'grab' }}
  >
    <DragHandleIcon />
  </IconButton>
));

DragHandle.propTypes = {
  listeners: PropTypes.object.isRequired,
  attributes: PropTypes.object.isRequired
};

const SortableItem = React.memo(({ item, sessionData, onSessionDataChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    alignItems: "center",
    gap: 2,
    p: 2,
    mb: 1,
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        '&:hover': {
          bgcolor: 'action.hover'
        },
        ...(item.isChapter ? {} : { pl: 4 })
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
        <IconButton size="small" disabled>
          {item.isChapter ? <Book color="primary" /> : <Audiotrack color="primary" />}
        </IconButton>
        <Box>
          <Typography variant="body2">{item.label}</Typography>
          <Typography variant="caption" color="text.secondary">
            {item.isChapter ? "Chapter" : "Session"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!sessionData[item.id]?.is_featured}
              onChange={() => onSessionDataChange(item.id, {
                ...(sessionData[item.id] || {}),
                is_featured: !sessionData[item.id]?.is_featured
              })}
            />
          }
          label={<Typography variant="body2">Featured</Typography>}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={!!sessionData[item.id]?.is_dark_mode}
              onChange={() => onSessionDataChange(item.id, {
                ...(sessionData[item.id] || {}),
                is_dark_mode: !sessionData[item.id]?.is_dark_mode
              })}
            />
          }
          label={<Typography variant="body2">Dark Mode</Typography>}
        />
        <DragHandle listeners={listeners} attributes={attributes} />
      </Box>
    </Box>
  );
});

SortableItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isChapter: PropTypes.bool
  }).isRequired,
  sessionData: PropTypes.object,
  onSessionDataChange: PropTypes.func
};

const SortableList = React.memo(({ items, sessionData, onSessionDataChange, onSortEnd }) => {
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      onSortEnd({ oldIndex, newIndex });
    }
  }, [items, onSortEnd]);

  if (!items || items.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        minHeight: '100px',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="body2" color="text.secondary">
          No items available
        </Typography>
      </Box>
    );
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              item={item}
              sessionData={sessionData}
              onSessionDataChange={onSessionDataChange}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
});

SortableList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isChapter: PropTypes.bool
  })).isRequired,
  sessionData: PropTypes.object,
  onSessionDataChange: PropTypes.func,
  onSortEnd: PropTypes.func.isRequired
};

export default SortableList; 